<template>
  <div ref="tooltipRef" class="match-tooltip" :class="`tooltip-${positionClassLocal}`" :style="localStyle">
    <div v-for="(game, index) in games" :key="game.id || index" class="tooltip-match"
      :class="{ 'tooltip-match-separator': index > 0 }">
      <div class="tooltip-header">
        <div class="tooltip-date-time">
          <span class="tooltip-date">{{ game.datetime }}</span>
          <span v-if="game.stage_name" class="tooltip-stage">{{ game.stage_name }}</span>
          <span v-if="game.tour_number" class="tooltip-tour">Тур {{ game.tour_number }}</span>
        </div>
        <div v-if="game.venue" class="tooltip-venue">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path
              d="M8 0C4.1 0 1 3.1 1 7c0 5.4 7 9 7 9s7-3.6 7-9c0-3.9-3.1-7-7-7zm0 9.5c-1.4 0-2.5-1.1-2.5-2.5S6.6 4.5 8 4.5s2.5 1.1 2.5 2.5S9.4 9.5 8 9.5z"
              fill="currentColor" />
          </svg>
          {{ game.venue }}
        </div>
      </div>

      <div class="tooltip-teams">
        <component
          :is="teamPublicPath(game.home_team) ? 'a' : 'div'"
          :href="teamPublicPath(game.home_team) || undefined"
          class="tooltip-team"
          :class="{ 'tooltip-team--link': !!teamPublicPath(game.home_team) }"
          @click.stop
        >
          <div class="tooltip-team-logo" v-if="game.home_team?.logo_path">
            <img :src="game.home_team.logo_path" :alt="game.home_team.name" />
          </div>
          <div class="tooltip-team-logo placeholder" v-else>
            {{ game.home_team?.name?.charAt(0) }}
          </div>
          <span class="tooltip-team-name">{{ game.home_team?.name }}</span>
          <span v-if="game.is_finished" class="tooltip-team-score">{{ game.home_score }}</span>
          <span v-else class="tooltip-team-vs">VS</span>
        </component>

        <div class="tooltip-vs">vs</div>

        <component
          :is="teamPublicPath(game.away_team) ? 'a' : 'div'"
          :href="teamPublicPath(game.away_team) || undefined"
          class="tooltip-team"
          :class="{ 'tooltip-team--link': !!teamPublicPath(game.away_team) }"
          @click.stop
        >
          <div class="tooltip-team-logo" v-if="game.away_team?.logo_path">
            <img :src="game.away_team.logo_path" :alt="game.away_team.name" />
          </div>
          <div class="tooltip-team-logo placeholder" v-else>
            {{ game.away_team?.name?.charAt(0) }}
          </div>
          <span class="tooltip-team-name">{{ game.away_team?.name }}</span>
          <span v-if="game.is_finished" class="tooltip-team-score">{{ game.away_score }}</span>
          <span v-else class="tooltip-team-vs">VS</span>
        </component>
      </div>

      <div v-if="!game.is_finished" class="tooltip-upcoming">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm0 14A6 6 0 1 1 8 2a6 6 0 0 1 0 12zm.5-9H7v4l3.5 2.1.5-.8-3-1.8V5z"
            fill="currentColor" />
        </svg>
        <span>Матч запланирован</span>
      </div>

      <div v-if="game.is_finished" class="tooltip-footer">
        <div v-if="game.sets_score && Array.isArray(game.sets_score) && game.sets_score.length > 0"
          class="tooltip-sets">
          <span class="tooltip-sets-label">Сеты:</span>
          <span class="tooltip-sets-score">{{ formatSetsScore(game.sets_score) }}</span>
        </div>
        <div v-if="game.attendance" class="tooltip-meta">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
            <path d="M8 8a3 3 0 100-6 3 3 0 000 6zm0 1c-2.67 0-8 1.34-8 4v1h16v-1c0-2.66-5.33-4-8-4z"
              fill="currentColor" />
          </svg>
          {{ game.attendance }} зрителей
        </div>
        <div v-if="game.referee" class="tooltip-meta">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
            <path d="M8 0L0 3v5c0 4.4 3.5 8.4 8 9 4.5-.6 8-4.6 8-9V3L8 0z" fill="currentColor" />
          </svg>
          {{ game.referee }}
        </div>
        <a v-if="game.match_video_url" :href="game.match_video_url" target="_blank" rel="noopener noreferrer"
          class="tooltip-video-link">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
            <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm4 8l-5-3v6l5-3z" fill="currentColor" />
          </svg>
          Ссылка на матч
        </a>
        <a v-if="game.has_stats" :href="gamePublicPath(game)" class="tooltip-video-link">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M1 14h2V6H1v8zm4 0h2V2H5v12zm4 0h2V8H9v6zm4 0h2V4h-2v10z" fill="currentColor" />
          </svg>
          Статистика матча
        </a>
        <span v-else class="tooltip-stats-unavailable" aria-disabled="true">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M1 14h2V6H1v8zm4 0h2V2H5v12zm4 0h2V8H9v6zm4 0h2V4h-2v10z" fill="currentColor" />
          </svg>
          Статистика недоступна
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { formatSetsScore } from '@/utils/matchHelpers';
import { gamePublicPath } from '@/utils/gameUrl';
import { teamPublicPath } from '@/utils/teamUrl';
import { ref, watch, nextTick, onMounted } from 'vue';

const props = defineProps({
  games: {
    type: Array,
    default: () => []
  },
  position: {
    type: Object,
    default: () => ({ x: 0, y: 0 })
  },
  positionClass: {
    type: String,
    default: 'top'
  }
  ,
  anchorRect: {
    type: Object,
    default: null
  }
});

const tooltipRef = ref(null);
const positionClassLocal = ref(props.positionClass);
const localStyle = ref({
  left: (props.position.x || 0) + 'px',
  top: (props.position.y || 0) + 'px',
  transform: props.positionClass === 'top' ? 'translate(-50%,-100%)' : 'translate(-50%,0)'
});

const clamp = (v, a, b) => Math.max(a, Math.min(v, b));

const adjustPosition = async () => {
  if (typeof window === 'undefined') return;

  await nextTick();
  const el = tooltipRef.value;
  if (!el) return;
  const w = el.offsetWidth;
  const h = el.offsetHeight;
  const margin = 8;

  // choose anchor rect if provided, otherwise fallback to position
  const rect = props.anchorRect || { left: (props.position.x || 0) - (w / 2), top: (props.position.y || 0), right: (props.position.x || 0) + (w / 2), bottom: (props.position.y || 0), width: 0, height: 0 };

  // center X based on rect center if available
  const anchorCenterX = props.anchorRect ? (rect.left + rect.width / 2) : (props.position.x || 0);
  const minCenterX = margin + w / 2;
  const maxCenterX = window.innerWidth - margin - w / 2;
  const centerX = clamp(anchorCenterX, minCenterX, Math.max(minCenterX, maxCenterX));

  // Try TOP
  let visualTop = (rect.top || 0) - h - margin;
  if (visualTop >= margin) {
    // fits on top
    positionClassLocal.value = 'top';
    localStyle.value.transform = 'translate(-50%,-100%)';
    localStyle.value.left = centerX + 'px';
    localStyle.value.top = (visualTop + h) + 'px';
    return;
  }

  // Try BOTTOM
  visualTop = (rect.bottom || (props.position.y || 0)) + margin;
  if (visualTop + h <= window.innerHeight - margin) {
    positionClassLocal.value = 'bottom';
    localStyle.value.transform = 'translate(-50%,0)';
    localStyle.value.left = centerX + 'px';
    localStyle.value.top = visualTop + 'px';
    return;
  }

  // Try LEFT
  let leftPos = (rect.left || (props.position.x || 0)) - w - margin;
  if (leftPos >= margin) {
    // vertical center for left placement
    const centerY = (rect.top || (props.position.y || 0)) + ((rect.height || 0) / 2);
    positionClassLocal.value = 'left';
    localStyle.value.transform = 'translate(-100%,-50%)';
    localStyle.value.left = (leftPos + w) + 'px'; // style.left equals right edge position
    localStyle.value.top = centerY + 'px';
    return;
  }

  // Fallback: place at clamped top (bottom preferred)
  // try bottom clamp
  let fallbackTop = clamp(props.position.y || 0, margin, window.innerHeight - margin - h);
  positionClassLocal.value = 'bottom';
  localStyle.value.transform = 'translate(-50%,0)';
  localStyle.value.left = centerX + 'px';
  localStyle.value.top = fallbackTop + 'px';
};

onMounted(() => {
  adjustPosition();
});

watch(() => [props.position.x, props.position.y, props.positionClass, props.games.length], adjustPosition);

</script>

<style lang="scss" scoped>
.match-tooltip {
  position: fixed;
  z-index: 1400;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.06);
  padding: 1rem;
  min-width: 300px;
  max-width: 90vw;
  pointer-events: auto;
  transition: opacity .12s ease, transform .12s ease;
  transform-origin: center;

  &.tooltip-top {
    transform: translate(-50%, -6px);
  }

  &.tooltip-bottom {
    transform: translate(-50%, 6px);
  }

  &.tooltip-left {
    transform: translate(-6px, -50%);
  }

  &.tooltip-right {
    transform: translate(6px, -50%);
  }

  // Header / meta
  .tooltip-header {
    margin-bottom: 0.5rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #eef2f7;

    .tooltip-date-time {
      display: flex;
      gap: .5rem;
      align-items: center;
    }

    .tooltip-date {
      font-weight: 600;
      color: #1e293b;
      font-size: 0.9rem;
    }

    .tooltip-stage,
    .tooltip-tour {
      font-size: 0.7rem;
      font-weight: 500;
      color: #64748b;
      background: #f1f5f9;
      padding: .2rem .4rem;
      border-radius: 4px;
    }

    .tooltip-venue {
      display: flex;
      gap: .4rem;
      color: #64748b;

      svg {
        color: #94a3b8;
      }
    }
  }

  // Teams block
  .tooltip-teams {
    display: flex;
    gap: .75rem;
    padding: .5rem;
    background: #f8fafc;
    border-radius: 8px;
    align-items: center;
    justify-content: space-between;
  }

  .tooltip-team {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: .5rem;
    flex: 1;

    &--link {
      color: inherit;
      text-decoration: none;

      &:hover .tooltip-team-name {
        text-decoration: underline;
      }
    }

    .tooltip-team-logo {
      width: 48px;
      height: 48px;
      border-radius: 6px;
      background: #f1f5f9;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }

    .tooltip-team-logo.placeholder {
      font-size: 1.125rem;
      color: #64748b;
    }

    .tooltip-team-name {
      font-weight: 600;
      color: #1e293b;
      text-align: center;
      font-size: 0.875rem;
    }

    .tooltip-team-score {
      font-weight: 700;
      color: #1e293b;
      font-size: 1.25rem;
    }
  }

  .tooltip-vs {
    font-weight: 600;
    color: #94a3b8;
    padding: 0 .5rem;
    align-self: center;
  }

  // Sets block
  .tooltip-sets {
    display: flex;
    align-items: center;
    gap: .5rem;
    padding: .5rem .75rem;
    background: #f1f5f9;
    border-radius: 6px;
    margin-bottom: 0; // removed per request
    font-size: 0.875rem;

    .tooltip-sets-label {
      font-weight: 600;
      color: #64748b;
    }

    .tooltip-sets-score {
      color: #1e293b;
      font-weight: 500;
    }
  }

  .tooltip-upcoming {
    display: flex;
    align-items: center;
    gap: .5rem;
    padding: .75rem;
    background: #eff6ff;
    border-radius: 6px;
    color: #3b82f6;
    font-weight: 500;
  }

  // Footer (vertical)
  .tooltip-footer {
    display: flex;
    flex-direction: column;
    gap: .5rem;
    padding-top: .5rem;
    border-top: 1px solid #f1f5f9;

    .tooltip-meta {
      display: flex;
      gap: .375rem;
      color: #64748b;
      align-items: center;

      svg {
        color: #94a3b8;
      }
    }

    .tooltip-video-link {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: .375rem;
      color: #3b82f6;
      text-decoration: none;
      font-weight: 500;

      &:hover {
        color: #2563eb;
      }
    }

    .tooltip-stats-unavailable {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: .375rem;
      color: #94a3b8;
      font-weight: 500;
      font-size: 0.875rem;
      cursor: default;
      user-select: none;

      svg {
        color: currentColor;
        opacity: 0.85;
      }
    }
  }

  .tooltip-team-vs {
    font-size: 0.75rem;
    color: #94a3b8;
    min-width: 30px;
    text-align: center;
  }
}
</style>